import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useNotificationSettings from '../../../hooks/useNotificationSettings';
import Loading from '../../layout/Loading';
import './SettingsPages.css';
import './PrivacySettings.css';
import './NotificationSettings.css';

function NotificationSettings() {
  const { t } = useTranslation(['profile', 'shared']);
  const { 
    settings, 
    isLoading, 
    error,
    updateNotificationSetting,
    toggleNotification
  } = useNotificationSettings();

  // Lokalne stany dla optymistycznych aktualizacji UI
  const [localSettings, setLocalSettings] = useState(settings);
  const [saving, setSaving] = useState(false);

  // Synchronizuj lokalne stany z Context gdy się zmienią
  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  // Sprawdź czy powiadomienie jest całkowicie wyłączone (OFF)
  const isNotificationOff = (notificationKey) => {
    const notif = localSettings[notificationKey];
    return !notif?.inApp && !notif?.email && !notif?.push;
  };

  // Przełącz powiadomienie ON/OFF
  const handleToggleNotification = async (key, event) => {
    // Sprawdź aktualny stan switcha przed kliknięciem
    // event.target.checked to nowy stan (po kliknięciu)
    const willBeOn = event.target.checked;
    
    // Optymistyczna aktualizacja UI
    if (willBeOn) {
      // Włącz - domyślnie tylko inApp
      setLocalSettings(prev => ({
        ...prev,
        [key]: { inApp: true, email: false, push: false }
      }));
    } else {
      // Wyłącz wszystkie typy
      setLocalSettings(prev => ({
        ...prev,
        [key]: { inApp: false, email: false, push: false }
      }));
    }

    try {
      setSaving(true);
      await toggleNotification(key, willBeOn);
    } catch (err) {
      console.error('Error toggling notification:', err);
      // Przywróć poprzednią wartość w przypadku błędu
      setLocalSettings(settings);
    } finally {
      setSaving(false);
    }
  };

  // Zmień konkretny typ powiadomienia
  const handleNotificationTypeChange = async (key, type, checked) => {
    // Optymistyczna aktualizacja UI
    setLocalSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [type]: checked
      }
    }));

    try {
      setSaving(true);
      await updateNotificationSetting(key, type, checked);
    } catch (err) {
      console.error('Error updating notification setting:', err);
      // Przywróć poprzednią wartość w przypadku błędu
      setLocalSettings(settings);
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="settings-page">
        <div className="settings-page-content">
          <Loading size="medium" />
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-page-content">
        {error && (
          <div className="notification-settings-error settings-inline-alert settings-inline-alert--error">
            {error}
          </div>
        )}
        {/* Wydarzenia */}
        <div className="privacy-settings-section">
          <h3 className="privacy-settings-section-title">
            {t('profile.settings.pages.notifications.events')}
          </h3>
          
          <div className="notification-settings-item">
            <div className="notification-settings-item-content">
              <div className="notification-settings-item-label">
                {t('profile.settings.pages.notifications.newEventNearby')}
              </div>
              <div className="notification-settings-item-description">
                {t('profile.settings.pages.notifications.newEventNearbyDescription')}
              </div>
            </div>
            <div className="notification-settings-controls">
              <label className="notification-switch">
                <input
                  type="checkbox"
                  checked={!isNotificationOff('newEventNearby')}
                  onChange={(e) => handleToggleNotification('newEventNearby', e)}
                  disabled={saving}
                />
                <span className="notification-switch-slider"></span>
              </label>
              <div className="notification-types">
                <label className={`notification-type-checkbox ${isNotificationOff('newEventNearby') ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={localSettings.newEventNearby?.inApp || false}
                    onChange={(e) => handleNotificationTypeChange('newEventNearby', 'inApp', e.target.checked)}
                    disabled={isNotificationOff('newEventNearby') || saving}
                  />
                  <span>{t('profile.settings.pages.notifications.typeInApp')}</span>
                </label>
                <label className={`notification-type-checkbox ${isNotificationOff('newEventNearby') ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={localSettings.newEventNearby?.email || false}
                    onChange={(e) => handleNotificationTypeChange('newEventNearby', 'email', e.target.checked)}
                    disabled={isNotificationOff('newEventNearby') || saving}
                  />
                  <span>{t('profile.settings.pages.notifications.typeEmail')}</span>
                </label>
                <label className={`notification-type-checkbox ${isNotificationOff('newEventNearby') ? 'disabled' : ''}`}>
              <input
                type="checkbox"
                    checked={localSettings.newEventNearby?.push || false}
                    onChange={(e) => handleNotificationTypeChange('newEventNearby', 'push', e.target.checked)}
                    disabled={isNotificationOff('newEventNearby') || saving}
              />
                  <span>{t('profile.settings.pages.notifications.typePush')}</span>
            </label>
              </div>
            </div>
          </div>
        </div>

        {/* Znajomi */}
        <div className="privacy-settings-section">
          <h3 className="privacy-settings-section-title">
            {t('profile.settings.pages.notifications.friends')}
          </h3>
          
          <div className="notification-settings-item">
            <div className="notification-settings-item-content">
              <div className="notification-settings-item-label">
                {t('profile.settings.pages.notifications.friendInvitation')}
              </div>
              <div className="notification-settings-item-description">
                {t('profile.settings.pages.notifications.friendInvitationDescription')}
              </div>
            </div>
            <div className="notification-settings-controls">
              <label className="notification-switch">
                <input
                  type="checkbox"
                  checked={!isNotificationOff('friendInvitation')}
                  onChange={(e) => handleToggleNotification('friendInvitation', e)}
                />
                <span className="notification-switch-slider"></span>
              </label>
              <div className="notification-types">
                <label className={`notification-type-checkbox ${isNotificationOff('friendInvitation') ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={localSettings.friendInvitation?.inApp || false}
                    onChange={(e) => handleNotificationTypeChange('friendInvitation', 'inApp', e.target.checked)}
                    disabled={isNotificationOff('friendInvitation') || saving}
                  />
                  <span>{t('profile.settings.pages.notifications.typeInApp')}</span>
                </label>
                <label className={`notification-type-checkbox ${isNotificationOff('friendInvitation') ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={localSettings.friendInvitation?.email || false}
                    onChange={(e) => handleNotificationTypeChange('friendInvitation', 'email', e.target.checked)}
                    disabled={isNotificationOff('friendInvitation') || saving}
                  />
                  <span>{t('profile.settings.pages.notifications.typeEmail')}</span>
                </label>
                <label className={`notification-type-checkbox ${isNotificationOff('friendInvitation') ? 'disabled' : ''}`}>
              <input
                type="checkbox"
                    checked={localSettings.friendInvitation?.push || false}
                    onChange={(e) => handleNotificationTypeChange('friendInvitation', 'push', e.target.checked)}
                    disabled={isNotificationOff('friendInvitation') || saving}
              />
                  <span>{t('profile.settings.pages.notifications.typePush')}</span>
            </label>
              </div>
            </div>
          </div>

          <div className="notification-settings-item">
            <div className="notification-settings-item-content">
              <div className="notification-settings-item-label">
                {t('profile.settings.pages.notifications.friendJoinedEvent')}
              </div>
              <div className="notification-settings-item-description">
                {t('profile.settings.pages.notifications.friendJoinedEventDescription')}
              </div>
            </div>
            <div className="notification-settings-controls">
              <label className="notification-switch">
                <input
                  type="checkbox"
                  checked={!isNotificationOff('friendJoinedEvent')}
                  onChange={(e) => handleToggleNotification('friendJoinedEvent', e)}
                />
                <span className="notification-switch-slider"></span>
              </label>
              <div className="notification-types">
                <label className={`notification-type-checkbox ${isNotificationOff('friendJoinedEvent') ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={localSettings.friendJoinedEvent?.inApp || false}
                    onChange={(e) => handleNotificationTypeChange('friendJoinedEvent', 'inApp', e.target.checked)}
                    disabled={isNotificationOff('friendJoinedEvent') || saving}
                  />
                  <span>{t('profile.settings.pages.notifications.typeInApp')}</span>
                </label>
                <label className={`notification-type-checkbox ${isNotificationOff('friendJoinedEvent') ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={localSettings.friendJoinedEvent?.email || false}
                    onChange={(e) => handleNotificationTypeChange('friendJoinedEvent', 'email', e.target.checked)}
                    disabled={isNotificationOff('friendJoinedEvent') || saving}
                  />
                  <span>{t('profile.settings.pages.notifications.typeEmail')}</span>
                </label>
                <label className={`notification-type-checkbox ${isNotificationOff('friendJoinedEvent') ? 'disabled' : ''}`}>
              <input
                type="checkbox"
                    checked={localSettings.friendJoinedEvent?.push || false}
                    onChange={(e) => handleNotificationTypeChange('friendJoinedEvent', 'push', e.target.checked)}
                    disabled={isNotificationOff('friendJoinedEvent') || saving}
              />
                  <span>{t('profile.settings.pages.notifications.typePush')}</span>
            </label>
              </div>
            </div>
          </div>
        </div>

        {/* Przypomnienia */}
        <div className="privacy-settings-section">
          <h3 className="privacy-settings-section-title">
            {t('profile.settings.pages.notifications.reminders')}
          </h3>
          
          <div className="notification-settings-item">
            <div className="notification-settings-item-content">
              <div className="notification-settings-item-label">
                {t('profile.settings.pages.notifications.eventStartsInHour')}
              </div>
              <div className="notification-settings-item-description">
                {t('profile.settings.pages.notifications.eventStartsInHourDescription')}
              </div>
            </div>
            <div className="notification-settings-controls">
              <label className="notification-switch">
                <input
                  type="checkbox"
                  checked={!isNotificationOff('eventStartsInHour')}
                  onChange={(e) => handleToggleNotification('eventStartsInHour', e)}
                />
                <span className="notification-switch-slider"></span>
              </label>
              <div className="notification-types">
                <label className={`notification-type-checkbox ${isNotificationOff('eventStartsInHour') ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={localSettings.eventStartsInHour?.inApp || false}
                    onChange={(e) => handleNotificationTypeChange('eventStartsInHour', 'inApp', e.target.checked)}
                    disabled={isNotificationOff('eventStartsInHour') || saving}
                  />
                  <span>{t('profile.settings.pages.notifications.typeInApp')}</span>
                </label>
                <label className={`notification-type-checkbox ${isNotificationOff('eventStartsInHour') ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={localSettings.eventStartsInHour?.email || false}
                    onChange={(e) => handleNotificationTypeChange('eventStartsInHour', 'email', e.target.checked)}
                    disabled={isNotificationOff('eventStartsInHour') || saving}
                  />
                  <span>{t('profile.settings.pages.notifications.typeEmail')}</span>
                </label>
                <label className={`notification-type-checkbox ${isNotificationOff('eventStartsInHour') ? 'disabled' : ''}`}>
              <input
                type="checkbox"
                    checked={localSettings.eventStartsInHour?.push || false}
                    onChange={(e) => handleNotificationTypeChange('eventStartsInHour', 'push', e.target.checked)}
                    disabled={isNotificationOff('eventStartsInHour') || saving}
              />
                  <span>{t('profile.settings.pages.notifications.typePush')}</span>
            </label>
              </div>
            </div>
          </div>
        </div>

        {/* Zgłoszenia */}
        <div className="privacy-settings-section">
          <h3 className="privacy-settings-section-title">
            {t('profile.settings.pages.notifications.applications')}
          </h3>
          
          <div className="notification-settings-item">
            <div className="notification-settings-item-content">
              <div className="notification-settings-item-label">
                {t('profile.settings.pages.notifications.applicationAccepted')}
              </div>
              <div className="notification-settings-item-description">
                {t('profile.settings.pages.notifications.applicationAcceptedDescription')}
              </div>
            </div>
            <div className="notification-settings-controls">
              <label className="notification-switch">
                <input
                  type="checkbox"
                  checked={!isNotificationOff('applicationAccepted')}
                  onChange={(e) => handleToggleNotification('applicationAccepted', e)}
                />
                <span className="notification-switch-slider"></span>
              </label>
              <div className="notification-types">
                <label className={`notification-type-checkbox ${isNotificationOff('applicationAccepted') ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={localSettings.applicationAccepted?.inApp || false}
                    onChange={(e) => handleNotificationTypeChange('applicationAccepted', 'inApp', e.target.checked)}
                    disabled={isNotificationOff('applicationAccepted') || saving}
                  />
                  <span>{t('profile.settings.pages.notifications.typeInApp')}</span>
                </label>
                <label className={`notification-type-checkbox ${isNotificationOff('applicationAccepted') ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={localSettings.applicationAccepted?.email || false}
                    onChange={(e) => handleNotificationTypeChange('applicationAccepted', 'email', e.target.checked)}
                    disabled={isNotificationOff('applicationAccepted') || saving}
                  />
                  <span>{t('profile.settings.pages.notifications.typeEmail')}</span>
                </label>
                <label className={`notification-type-checkbox ${isNotificationOff('applicationAccepted') ? 'disabled' : ''}`}>
              <input
                type="checkbox"
                    checked={localSettings.applicationAccepted?.push || false}
                    onChange={(e) => handleNotificationTypeChange('applicationAccepted', 'push', e.target.checked)}
                    disabled={isNotificationOff('applicationAccepted') || saving}
              />
                  <span>{t('profile.settings.pages.notifications.typePush')}</span>
            </label>
              </div>
            </div>
          </div>
        </div>

        {/* Moje wydarzenia */}
        <div className="privacy-settings-section">
          <h3 className="privacy-settings-section-title">
            {t('profile.settings.pages.notifications.myEvents')}
          </h3>
          
          <div className="notification-settings-item">
            <div className="notification-settings-item-content">
              <div className="notification-settings-item-label">
                {t('profile.settings.pages.notifications.eventCreated')}
              </div>
              <div className="notification-settings-item-description">
                {t('profile.settings.pages.notifications.eventCreatedDescription')}
              </div>
            </div>
            <div className="notification-settings-controls">
              <label className="notification-switch">
                <input
                  type="checkbox"
                  checked={!isNotificationOff('eventCreated')}
                  onChange={(e) => handleToggleNotification('eventCreated', e)}
                />
                <span className="notification-switch-slider"></span>
              </label>
              <div className="notification-types">
                <label className={`notification-type-checkbox ${isNotificationOff('eventCreated') ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={localSettings.eventCreated?.inApp || false}
                    onChange={(e) => handleNotificationTypeChange('eventCreated', 'inApp', e.target.checked)}
                    disabled={isNotificationOff('eventCreated') || saving}
                  />
                  <span>{t('profile.settings.pages.notifications.typeInApp')}</span>
                </label>
                <label className={`notification-type-checkbox ${isNotificationOff('eventCreated') ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={localSettings.eventCreated?.email || false}
                    onChange={(e) => handleNotificationTypeChange('eventCreated', 'email', e.target.checked)}
                    disabled={isNotificationOff('eventCreated') || saving}
                  />
                  <span>{t('profile.settings.pages.notifications.typeEmail')}</span>
                </label>
                <label className={`notification-type-checkbox ${isNotificationOff('eventCreated') ? 'disabled' : ''}`}>
              <input
                type="checkbox"
                    checked={localSettings.eventCreated?.push || false}
                    onChange={(e) => handleNotificationTypeChange('eventCreated', 'push', e.target.checked)}
                    disabled={isNotificationOff('eventCreated') || saving}
              />
                  <span>{t('profile.settings.pages.notifications.typePush')}</span>
            </label>
              </div>
            </div>
          </div>

          <div className="notification-settings-item">
            <div className="notification-settings-item-content">
              <div className="notification-settings-item-label">
                {t('profile.settings.pages.notifications.newApplicationToEvent')}
              </div>
              <div className="notification-settings-item-description">
                {t('profile.settings.pages.notifications.newApplicationToEventDescription')}
              </div>
            </div>
            <div className="notification-settings-controls">
              <label className="notification-switch">
                <input
                  type="checkbox"
                  checked={!isNotificationOff('newApplicationToEvent')}
                  onChange={(e) => handleToggleNotification('newApplicationToEvent', e)}
                />
                <span className="notification-switch-slider"></span>
              </label>
              <div className="notification-types">
                <label className={`notification-type-checkbox ${isNotificationOff('newApplicationToEvent') ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={localSettings.newApplicationToEvent?.inApp || false}
                    onChange={(e) => handleNotificationTypeChange('newApplicationToEvent', 'inApp', e.target.checked)}
                    disabled={isNotificationOff('newApplicationToEvent') || saving}
                  />
                  <span>{t('profile.settings.pages.notifications.typeInApp')}</span>
                </label>
                <label className={`notification-type-checkbox ${isNotificationOff('newApplicationToEvent') ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={localSettings.newApplicationToEvent?.email || false}
                    onChange={(e) => handleNotificationTypeChange('newApplicationToEvent', 'email', e.target.checked)}
                    disabled={isNotificationOff('newApplicationToEvent') || saving}
                  />
                  <span>{t('profile.settings.pages.notifications.typeEmail')}</span>
                </label>
                <label className={`notification-type-checkbox ${isNotificationOff('newApplicationToEvent') ? 'disabled' : ''}`}>
              <input
                type="checkbox"
                    checked={localSettings.newApplicationToEvent?.push || false}
                    onChange={(e) => handleNotificationTypeChange('newApplicationToEvent', 'push', e.target.checked)}
                    disabled={isNotificationOff('newApplicationToEvent') || saving}
              />
                  <span>{t('profile.settings.pages.notifications.typePush')}</span>
            </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotificationSettings;
